import { useState, useEffect } from "react";

import { format } from "date-fns";
import { RefreshCw, Eye, MapPin, Monitor, Globe, Shield, AlertTriangle, ArrowUpDown } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { getBrowserInfo, getOSInfo } from "@/lib/sessionInfoFormatter";

import { RedactedEmail } from "./RedactedEmail";

interface EmailAccessLog {
  id: string;
  admin_user_id: string;
  admin_email: string;
  viewed_user_id: string;
  viewed_email: string;
  ip_address: string;
  user_agent: string;
  request_headers: any;
  country: string | null;
  country_code: string | null;
  region: string | null;
  region_name: string | null;
  city: string | null;
  zip: string | null;
  latitude: number | null;
  longitude: number | null;
  timezone: string | null;
  isp: string | null;
  org: string | null;
  as_number: string | null;
  as_name: string | null;
  reverse_dns: string | null;
  mobile: boolean | null;
  proxy: boolean | null;
  hosting: boolean | null;
  threat_score?: number | null;
  threat_level?: string | null;
  threat_categories?: any;
  abuse_confidence_score?: number | null;
  is_tor?: boolean | null;
  is_vpn?: boolean | null;
  is_datacenter?: boolean | null;
  threat_intel_source?: string | null;
  threat_checked_at?: string | null;
  enrichment_status?: string | null;
  created_at: string;
}

const redactEmail = (email: string): string => {
  if (!email || !email.includes("@")) return email;
  
  const [username, domain] = email.split("@");
  if (username.length <= 2) {
    return `${"*".repeat(username.length)}@${domain}`;
  }
  
  const visiblePart = username.slice(-2);
  const hiddenPart = "*".repeat(username.length - 2);
  return `${hiddenPart}${visiblePart}@${domain}`;
};

export const EmailAccessAudit = () => {
  const [logs, setLogs] = useState<EmailAccessLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [threatFilter, setThreatFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"date" | "threat">("date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const fetchLogs = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from("admin_email_access_audit")
        .select("*");
      
      // Apply threat level filter
      if (threatFilter !== "all") {
        query = query.eq('threat_level', threatFilter);
      }
      
      // Apply sorting
      if (sortBy === "threat") {
        query = query.order('threat_score', { ascending: sortOrder === "asc", nullsFirst: false });
      } else {
        query = query.order('created_at', { ascending: sortOrder === "asc" });
      }
      
      query = query.limit(100);
      
      const { data, error } = await query;

      if (error) throw error;
      setLogs((data || []) as EmailAccessLog[]);
    } catch (error) {
      console.error("Error fetching email access logs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSort = (newSortBy: "date" | "threat") => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(newSortBy);
      setSortOrder("desc");
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threatFilter, sortBy, sortOrder]);

  useEffect(() => {

    // Set up real-time subscription
    const channel = supabase
      .channel("email-access-logs")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "admin_email_access_logs",
        },
        () => {
          fetchLogs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Note: getBrowserInfo and getOSInfo are now imported from @/lib/sessionInfoFormatter

  const getLocationString = (log: EmailAccessLog): string => {
    const parts = [];
    if (log.city) parts.push(log.city);
    if (log.region_name) parts.push(log.region_name);
    if (log.country) parts.push(log.country);
    return parts.length > 0 ? parts.join(", ") : "Unknown location";
  };

  const getCountryFlag = (countryCode: string | null): string => {
    if (!countryCode) return "🌍";
    const codePoints = countryCode
      .toUpperCase()
      .split("")
      .map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  };

  const getThreatBadge = (log: EmailAccessLog) => {
    if (log.enrichment_status === 'queued') {
      return <Badge variant="outline" className="text-xs">⏳ Queued</Badge>;
    }
    if (log.enrichment_status === 'pending') {
      return <Badge variant="outline" className="text-xs">⏱️ Pending</Badge>;
    }
    if (!log.threat_level) return null;

    switch (log.threat_level) {
      case 'malicious':
        return (
          <Badge variant="destructive" className="text-xs">
            🔴 Malicious ({log.threat_score})
          </Badge>
        );
      case 'suspicious':
        return (
          <Badge className="text-xs bg-orange-500 hover:bg-orange-600">
            🟡 Suspicious ({log.threat_score})
          </Badge>
        );
      case 'safe':
        return (
          <Badge variant="outline" className="text-xs border-green-500 text-green-700 dark:text-green-400">
            🟢 Safe
          </Badge>
        );
      default:
        return <Badge variant="outline" className="text-xs">⚪ Unknown</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Email Access Audit Log
            </CardTitle>
            <CardDescription>
              Track when admins view full email addresses with geolocation and network info
            </CardDescription>
          </div>
          <Button onClick={fetchLogs} disabled={isLoading} size="sm" variant="outline">
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        <div className="flex flex-wrap gap-4 mt-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Threat Level:</label>
            <Select value={threatFilter} onValueChange={setThreatFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="safe">🟢 Safe</SelectItem>
                <SelectItem value="suspicious">🟡 Suspicious</SelectItem>
                <SelectItem value="malicious">🔴 Malicious</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Sort By:</label>
            <Button
              variant={sortBy === "date" ? "default" : "outline"}
              size="sm"
              onClick={() => toggleSort("date")}
            >
              Date
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
            <Button
              variant={sortBy === "threat" ? "default" : "outline"}
              size="sm"
              onClick={() => toggleSort("threat")}
            >
              Threat Score
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
            {sortBy && (
              <Badge variant="outline" className="text-xs">
                {sortOrder === "desc" ? "↓ Descending" : "↑ Ascending"}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {logs.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No email access recorded yet.
          </p>
        ) : (
          <ScrollArea className="h-[500px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Admin</TableHead>
                  <TableHead>Viewed Email</TableHead>
                  <TableHead>Threat Level</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Network</TableHead>
                  <TableHead>Device</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-sm whitespace-nowrap">
                      {format(new Date(log.created_at), "MMM d, yyyy HH:mm:ss")}
                    </TableCell>
                    <TableCell>
                      <RedactedEmail 
                        userId={log.admin_user_id} 
                        redactedEmail={redactEmail(log.admin_email)} 
                      />
                    </TableCell>
                    <TableCell>
                      <RedactedEmail 
                        userId={log.viewed_user_id} 
                        redactedEmail={redactEmail(log.viewed_email)} 
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getThreatBadge(log)}
                        {log.abuse_confidence_score && log.abuse_confidence_score > 0 && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge variant="outline" className="text-xs">
                                  AbuseIPDB: {log.abuse_confidence_score}%
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">Abuse Confidence Score from AbuseIPDB</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{getCountryFlag(log.country_code)}</span>
                              <div className="text-left">
                                <div className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-sm font-medium">{getLocationString(log)}</span>
                                </div>
                                {log.timezone && (
                                  <p className="text-xs text-muted-foreground">{log.timezone}</p>
                                )}
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="space-y-1 text-xs">
                              {log.latitude && log.longitude && (
                                <p>Coordinates: {log.latitude.toFixed(4)}, {log.longitude.toFixed(4)}</p>
                              )}
                              {log.zip && <p>ZIP: {log.zip}</p>}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Globe className="h-3 w-3 text-muted-foreground" />
                                <div className="flex gap-1">
                                  {log.is_tor && (
                                    <Badge variant="destructive" className="text-xs">
                                      🧅 Tor
                                    </Badge>
                                  )}
                                  {log.is_vpn && (
                                    <Badge variant="destructive" className="text-xs">
                                      🛡️ VPN
                                    </Badge>
                                  )}
                                  {log.proxy && (
                                    <Badge variant="destructive" className="text-xs">
                                      <Shield className="h-2 w-2 mr-1" />
                                      Proxy
                                    </Badge>
                                  )}
                                  {log.hosting && (
                                    <Badge variant="secondary" className="text-xs">
                                      Hosting
                                    </Badge>
                                  )}
                                  {log.mobile && (
                                    <Badge variant="outline" className="text-xs">
                                      Mobile
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div className="text-sm font-mono">{log.ip_address}</div>
                              {log.isp && (
                                <p className="text-xs text-muted-foreground truncate max-w-[150px]">
                                  {log.isp}
                                </p>
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-md">
                            <div className="space-y-2 text-xs">
                              <div>
                                <p className="font-semibold">Network Information</p>
                                {log.isp && <p>ISP: {log.isp}</p>}
                                {log.org && <p>Organization: {log.org}</p>}
                                {log.as_number && <p>AS Number: {log.as_number}</p>}
                                {log.as_name && <p>AS Name: {log.as_name}</p>}
                                {log.reverse_dns && <p>Reverse DNS: {log.reverse_dns}</p>}
                              </div>
                              {(log.proxy || log.hosting || log.mobile) && (
                                <div>
                                  <p className="font-semibold">Flags</p>
                                  {log.proxy && <p className="text-red-600">⚠️ Proxy detected</p>}
                                  {log.hosting && <p>🖥️ Hosting provider</p>}
                                  {log.mobile && <p>📱 Mobile network</p>}
                                </div>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Monitor className="h-3 w-3 text-muted-foreground" />
                        <div className="space-y-1">
                          <div className="flex gap-2">
                            <Badge variant="outline" className="text-xs">
                              {getBrowserInfo(log.user_agent)}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {getOSInfo(log.user_agent)}
                            </Badge>
                          </div>
                          {log.request_headers?.['sec-ch-ua-platform'] && (
                            <p className="text-xs text-muted-foreground">
                              {log.request_headers['sec-ch-ua-platform'].replace(/"/g, '')}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};
